import type { TradeEntryFormData, TradeValidationError } from './trade-types';

// Out-of-band FX rates surface a non-blocking warning. Band kept wide
// because real-world rates like IDR/USD (~0.000063), KRW/USD (~0.00072),
// and JPY/USD (~0.0068) fall well below an "intuitive" lower bound; values
// that are truly mis-typed (inverted pair, missing decimal) typically land
// far outside this band. Shared with the inline warning under the FX Rate
// input in CreateTradeModal so the two surfacings always agree (LR-371).
export const FX_RATE_UNUSUAL_MIN = 0.001;
export const FX_RATE_UNUSUAL_MAX = 500;

export const FX_RATE_UNUSUAL_MESSAGE = 'FX Rate seems unusual. Please verify.';

// Shared with the FX Rate field validator: without a rate the API rejects a
// cross-currency booking rather than defaulting it to 1.0.
export const FX_RATE_REQUIRED_MESSAGE = 'FX Rate is required for cross-currency settlement';

// Inline warning under the FX Rate input. Returns the message when the rate
// is strictly positive and out-of-band, otherwise undefined. Negative rates
// already raise a hard error from the field validator — they do NOT also
// trigger this warning (no duplicate user-facing message). Non-number /
// zero / NaN inputs return undefined so transient typing states ("0",
// "0.000") don't flash a warning before the user finishes a sub-unit rate.
export const getFxRateInlineWarning = (value: unknown): string | undefined => {
  if (typeof value !== 'number') return undefined;
  if (!(value > 0)) return undefined;
  if (value > FX_RATE_UNUSUAL_MAX || value < FX_RATE_UNUSUAL_MIN) {
    return FX_RATE_UNUSUAL_MESSAGE;
  }
  return undefined;
};

// Returns 'Saturday'/'Sunday' if the ISO date (YYYY-MM-DD) lands on a weekend,
// otherwise undefined. Parsed via Date.UTC so the weekday is timezone-stable: a
// date-only string read in a negative-offset locale would otherwise shift a day
// and misreport which weekday (Sat/Sun) it falls on. Shared with the inline
// per-field warnings in PositionDatesSection so the two surfacings always agree.
export const getWeekendDayName = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  const dayOfWeek = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  if (dayOfWeek === 0) return 'Sunday';
  if (dayOfWeek === 6) return 'Saturday';
  return undefined;
};

export const validateTradeEntry = (formData: Partial<TradeEntryFormData>): TradeValidationError[] => {
  const errors: TradeValidationError[] = [];
  if (!formData.position?.tradeType) {
    errors.push({ field: 'position.tradeType', message: 'Trade Type is required', severity: 'error' });
  }
  if (!formData.position?.collateralType) {
    errors.push({ field: 'position.collateralType', message: 'Collateral Type is required', severity: 'error' });
  }
  if (!formData.security?.isin) {
    errors.push({ field: 'security.isin', message: 'ISIN is required', severity: 'error' });
  }
  const quantity = formData.position?.quantity || 0;
  if (quantity <= 0) {
    errors.push({ field: 'position.quantity', message: 'Quantity must be greater than 0', severity: 'error' });
  }
  if (formData.position?.availableQuantity && quantity > formData.position.availableQuantity) {
    errors.push({
      field: 'position.quantity',
      message: `Quantity exceeds available (${formData.position.availableQuantity})`,
      severity: 'error',
    });
  }
  if (!formData.position?.contractPrice || formData.position.contractPrice <= 0) {
    errors.push({ field: 'position.contractPrice', message: 'Contract Price is required', severity: 'error' });
  }

  if (!formData.rate?.rateEntryMode) {
    errors.push({ field: 'rate.rateEntryMode', message: 'Rate Entry Mode is required', severity: 'error' });
  } else if (formData.rate.rateEntryMode === 'fee') {
    if (formData.rate.feePercent === undefined || formData.rate.feePercent === null) {
      errors.push({ field: 'rate.feePercent', message: 'Fee % is required', severity: 'error' });
    } else if (formData.rate.feePercent <= 0) {
      errors.push({ field: 'rate.feePercent', message: 'Fee must be positive', severity: 'error' });
    }
    if (!formData.rate.benchmark) {
      errors.push({ field: 'rate.benchmark', message: 'Benchmark is required for Fee mode', severity: 'error' });
    }
  } else if (formData.rate.rateEntryMode === 'rebate') {
    if (formData.rate?.rateType === 'fixed') {
      const r = formData.rate?.fixedRate;
      // Treat only undefined/null/NaN as missing. 0 and negatives are
      // legitimate user input — see LR-343 (negative rebate rates allowed).
      if (r === undefined || r === null || (typeof r === 'number' && isNaN(r))) {
        errors.push({ field: 'rate.fixedRate', message: 'Fixed Rate is required', severity: 'error' });
      }
    }
    if (formData.rate?.rateType === 'floating') {
      if (formData.rate?.spreadBps === undefined || formData.rate?.spreadBps === null) {
        errors.push({ field: 'rate.spreadBps', message: 'Spread (bps) is required for floating rate', severity: 'error' });
      }
      if (!formData.rate?.benchmark) {
        errors.push({ field: 'rate.benchmark', message: 'Benchmark is required for floating rate', severity: 'error' });
      }
      // LR-364's positivity guard removed (LR-365); LR-464: rebate = benchmark +
      // spread/100 is permitted to go negative for special stocks (negative spread).
    }
  }

  if (!formData.counterparty?.name) {
    errors.push({ field: 'counterparty.name', message: 'Counterparty is required', severity: 'error' });
  }
  // No framework-agreement rule (LR-634): it is derived from `tradeType`, and the
  // counterparty's registry mark gates nothing.
  if (!formData.dates?.tradeDate) {
    errors.push({ field: 'dates.tradeDate', message: 'Trade Date is required', severity: 'error' });
  }
  if (!formData.dates?.settlementDate) {
    errors.push({ field: 'dates.settlementDate', message: 'Settlement Date is required', severity: 'error' });
  }
  if (formData.dates?.tradeDate && formData.dates?.settlementDate) {
    if (formData.dates.settlementDate < formData.dates.tradeDate) {
      errors.push({
        field: 'dates.settlementDate',
        message: 'Settlement Date cannot be before Trade Date',
        severity: 'error',
      });
    }
  }
  const tradeDuration = formData.dates?.tradeDuration || 'OPEN';
  if (tradeDuration === 'TERM') {
    if (!formData.dates?.maturityDate) {
      errors.push({
        field: 'dates.maturityDate',
        message: 'Maturity date is required for TERM trades',
        severity: 'error',
      });
    } else if (
      formData.dates?.settlementDate &&
      formData.dates.maturityDate < formData.dates.settlementDate
    ) {
      // LR-462: interest accrues over [settlement, maturity), so a maturity before
      // the settlement date is invalid (the backend serializer rejects it too).
      // maturity === settlement is allowed — a degenerate 0-day term.
      errors.push({
        field: 'dates.maturityDate',
        message: 'Maturity date cannot be before settlement date',
        severity: 'error',
      });
    } else if (formData.dates?.tradeDate && formData.dates.maturityDate < formData.dates.tradeDate) {
      // Fallback bound when no settlement date is set yet. Uses `<` (not `<=`)
      // so a degenerate T+0 0-day term (tradeDate === settlement === maturity)
      // clears this gate: the settlement check above already owns the
      // maturity-vs-settlement bound, and the backend serializer accepts such a
      // term — it requires maturity >= settlement only, never maturity > trade
      // date. With `<=` the form over-rejected a same-day term the API allows.
      errors.push({
        field: 'dates.maturityDate',
        message: 'Maturity date must be after trade date',
        severity: 'error',
      });
    }
  }
  if (tradeDuration === 'EVERGREEN') {
    if (formData.dates?.maturityDate) {
      if (formData.dates?.tradeDate && formData.dates.maturityDate <= formData.dates.tradeDate) {
        errors.push({
          field: 'dates.maturityDate',
          message: 'Maturity date must be after trade date',
          severity: 'error',
        });
      }
    }
    if (formData.dates?.noticePeriodDays === undefined || formData.dates.noticePeriodDays < 0) {
      errors.push({
        field: 'dates.noticePeriodDays',
        message: 'Notice period is required for EVERGREEN trades',
        severity: 'error',
      });
    }
  }
  // Soft, non-blocking weekend warnings per date field (feedback p.3 / LR-450).
  // Surfaced here in the submit-time banner AND inline under each field in
  // PositionDatesSection via the same helper, so the two stay in sync.
  const weekendDateFields: Array<{ field: string; label: string; iso?: string }> = [
    { field: 'dates.tradeDate', label: 'Trade Date', iso: formData.dates?.tradeDate },
    { field: 'dates.settlementDate', label: 'Settlement Date', iso: formData.dates?.settlementDate },
    { field: 'dates.maturityDate', label: 'Maturity Date', iso: formData.dates?.maturityDate },
  ];
  for (const { field, label, iso } of weekendDateFields) {
    const weekendDay = getWeekendDayName(iso);
    if (weekendDay) {
      errors.push({
        field,
        message: `${label} falls on a weekend (${weekendDay})`,
        severity: 'warning',
      });
    }
  }
  if (!formData.settlement?.deliveryType) {
    errors.push({ field: 'settlement.deliveryType', message: 'Delivery Type is required', severity: 'error' });
  }
  if (!formData.settlement?.venue) {
    errors.push({ field: 'settlement.venue', message: 'Settlement Venue is required', severity: 'error' });
  }
  // No data → no verdict. Earlier this defaulted security.currency to 'USD'
  // when missing, which produced a spurious "USD → EUR" warning whenever the
  // caller forgot to pipe currency through (see LR-318 follow-up).
  const rawSecurityCurrency = formData.security?.currency?.trim().toUpperCase();
  const rawSettlementCurrency = formData.settlement?.settlementCurrency?.trim().toUpperCase();
  if (rawSecurityCurrency && rawSettlementCurrency && rawSecurityCurrency !== rawSettlementCurrency) {
    errors.push({
      field: 'settlement.settlementCurrency',
      message: `Cross-currency settlement: ${rawSecurityCurrency} → ${rawSettlementCurrency}. Verify FX rate.`,
      severity: 'warning',
    });
    // Cash-matching writes its derived rate back into fx_rate, so it lands here too.
    if (formData.settlement?.fxRate === undefined) {
      errors.push({ field: 'settlement.fxRate', message: FX_RATE_REQUIRED_MESSAGE, severity: 'error' });
    }
  }
  if (formData.settlement?.fxRate !== undefined) {
    if (formData.settlement.fxRate <= 0) {
      errors.push({
        field: 'settlement.fxRate',
        message: 'FX Rate must be greater than 0',
        severity: 'error',
      });
    } else if (
      formData.settlement.fxRate > FX_RATE_UNUSUAL_MAX ||
      formData.settlement.fxRate < FX_RATE_UNUSUAL_MIN
    ) {
      errors.push({
        field: 'settlement.fxRate',
        message: `FX Rate ${formData.settlement.fxRate} seems unusual. Please verify.`,
        severity: 'warning',
      });
    }
  }
  // LR-634: no rule pairing the trade type with the counterparty's registry
  // agreement — one counterparty may hold both GMSLA and GMRA, so a lend on a
  // GMRA-registered counterparty is a normal booking. The trade's own agreement
  // is derived from its type, and the pool is checked against that.
  const tradeType = formData.position?.tradeType;
  // LR-617: fee rate entry is a GMSLA-only mode — repos always book as rebate.
  // Keyed on trade_type alone (no agreement needed), matching the API guard.
  if ((tradeType === 'repo' || tradeType === 'reverse_repo') && formData.rate?.rateEntryMode === 'fee') {
    errors.push({
      field: 'rate.rateEntryMode',
      message: 'Fee rate entry is not available for Repo/Reverse Repo trades',
      severity: 'error',
    });
  }
  // LR-844: a repo is a sale against cash — Repo / Reverse Repo take Cash
  // collateral only, matching the API guard.
  const collateralType = formData.position?.collateralType;
  if ((tradeType === 'repo' || tradeType === 'reverse_repo') && collateralType && collateralType !== 'cash') {
    errors.push({
      field: 'position.collateralType',
      message: 'Non-cash and Uncollateralized collateral are not available for Repo/Reverse Repo trades',
      severity: 'error',
    });
  }
  if (formData.rate?.dividendPassThrough !== undefined) {
    if (formData.rate.dividendPassThrough < 0 || formData.rate.dividendPassThrough > 100) {
      errors.push({
        field: 'rate.dividendPassThrough',
        message: 'Dividend Pass-Through must be between 0-100%',
        severity: 'error',
      });
    }
    if (formData.rate.dividendPassThrough < 85) {
      errors.push({
        field: 'rate.dividendPassThrough',
        message: 'Dividend Pass-Through below 85% may be suboptimal',
        severity: 'warning',
      });
    }
  }
  // Skip the benchmark-fee comparison for negative rates: deep-special trades
  // legitimately go negative (LR-343), and the warning would always fire there
  // since any negative is below `contractPrice * 0.01`.
  if (formData.rate?.fixedRate && formData.rate.fixedRate > 0 && formData.position?.contractPrice) {
    const benchmarkFee = formData.position.contractPrice * 0.01;
    if (formData.rate.fixedRate < benchmarkFee) {
      errors.push({
        field: 'rate.fixedRate',
        message: 'Rate is below benchmark fee',
        severity: 'warning',
      });
    }
  }
  return errors;
};
